import {
  Card,
  Text,
  Stack,
  Divider,
  SectionMessage,
} from "@certn/cascadia-react";
import { ArrayControlProps, JsonSchema4, JsonSchema7 } from "@jsonforms/core";
import { useTranslation } from "react-i18next";
import { useContext, useState } from "react";
import set from "lodash/set";
import cloneDeep from "lodash/cloneDeep";
import { css } from "@emotion/css";

import {
  CascadiaJsonFormContext,
  ValidationError,
} from "@certn/forms/src/CascadiaJsonFormContext";
import { useApplication } from "../../ApplicationContext";
import { GapStatement } from "./GapStatement";
import {
  Gap,
  GapActivityType,
  GapDocumentationType,
  GapReferenceType,
} from "./types";
import {
  GAP_HISTORY_STORAGE_KEY,
  isGapStatementActivity,
  stringifyGaps,
} from "./utils";
import { useGapTitle } from "./hooks";
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import { GapReference } from "./GapReference";
import { GapDocumentation } from "./GapDocumentation";

export function GapHistoryControl(props: ArrayControlProps) {
  const { navigateToSection } = useApplication();
  const { t } = useTranslation();
  const { values, setValues, submitErrors } = useContext(
    CascadiaJsonFormContext
  );
  const includesEducation = Boolean(values.education_history);

  const gaps = (props.data || []) as Gap[];
  const gap_reference_schema: JsonSchema4 | JsonSchema7 | undefined =
    props.schema
      ? props.schema.properties?.["gap_reference"]?.properties
      : undefined;
  const items = props.schema?.properties?.["gap_documentation"]?.items;

  const gap_documentation_schema:
    | Record<string, JsonSchema4 | JsonSchema7>
    | undefined = items && !Array.isArray(items) ? items.properties : undefined;

  const lookback_validation_errors: ValidationError =
    submitErrors["gap_history.is_lookback_period_covered"];
  const is_lookback_period_covered_error = Array.isArray(
    lookback_validation_errors
  )
    ? lookback_validation_errors[0]
    : lookback_validation_errors;

  const [hasGapsChanged] = useState(() => {
    const savedState = localStorage.getItem(GAP_HISTORY_STORAGE_KEY);
    const currentState = stringifyGaps(gaps);
    return savedState !== null && savedState !== currentState;
  });

  const handleChange = async (
    gapIndex: number,
    input: Pick<Gap, "reason" | "gap_reference" | "gap_documentation">
  ) => {
    const updatedGaps = gaps.map((gap, index) => {
      return index === gapIndex ? { ...gap, ...omitBy(input, isNil) } : gap;
    });
    const updatedValues = cloneDeep(values);

    set(updatedValues, props.path, updatedGaps);
    setValues(updatedValues);
    localStorage.setItem(GAP_HISTORY_STORAGE_KEY, stringifyGaps(updatedGaps));
  };

  return (
    <Stack gap="space-4">
      {gaps.length === 0 && (
        <SectionMessage
          type="success"
          sectionTitle={t(
            "No gaps were identified in your employment and/or education history."
          )}
          icon="verified_user"
        ></SectionMessage>
      )}

      {hasGapsChanged && gaps.length > 0 && (
        <SectionMessage
          type="info"
          sectionTitle={t("Your experience gaps were updated")}
          icon="info"
        >
          {t(
            "We've refreshed this section based on changes to your employment or education history. Any information entered for gaps that no longer apply may have been cleared."
          )}
        </SectionMessage>
      )}

      {gaps.map((gap, index) => (
        <GapCard
          key={`${gap.start_date}-${gap.end_date}`}
          gap={gap}
          index={index}
          onReasonChange={(gapIndex, reason) =>
            handleChange(gapIndex, { reason })
          }
          onNavigateToSection={navigateToSection}
          gap_reference_schema={gap_reference_schema}
          onGapReferenceChange={(gapIndex, gap_reference) =>
            handleChange(gapIndex, { gap_reference })
          }
          includesEducation={includesEducation}
          gap_documentation_schema={gap_documentation_schema}
          onGapDocumentationChange={(gapIndex, gap_documentation) =>
            handleChange(gapIndex, { gap_documentation })
          }
        />
      ))}

      {is_lookback_period_covered_error && (
        <SectionMessage type="danger" icon="error">
          {is_lookback_period_covered_error}
        </SectionMessage>
      )}
    </Stack>
  );
}

function GapCard({
  gap,
  index,
  onReasonChange,
  onNavigateToSection,
  gap_reference_schema,
  onGapReferenceChange,
  includesEducation,
  gap_documentation_schema,
  onGapDocumentationChange,
}: {
  gap: Gap;
  index: number;
  onReasonChange: (gapIndex: number, reason: string) => void;
  onGapReferenceChange: (
    gapIndex: number,
    gap_reference: Partial<GapReferenceType>
  ) => void;
  onNavigateToSection: (section: string) => void;
  gap_reference_schema?: JsonSchema4 | JsonSchema7;
  gap_documentation_schema?: Record<string, JsonSchema4 | JsonSchema7>;
  onGapDocumentationChange: (
    gapIndex: number,
    gap_documentation: GapDocumentationType
  ) => void;
  includesEducation: boolean;
}) {
  const [activityType, setActivityType] = useState<GapActivityType>(
    gap.reason ? "OTHER" : ""
  );

  const title = useGapTitle(gap);
  const gap_reference = gap.gap_reference;
  const gap_documentation = gap.gap_documentation;
  const isStatementRequired = isGapStatementActivity(
    activityType,
    includesEducation
  );
  const isGapReferenceRequired =
    !!gap_reference_schema && !!gap_reference && isStatementRequired;
  const isGapDocumentationRequired =
    !!gap_documentation_schema && !!gap_documentation && isStatementRequired;

  return (
    <Card className={styles.card} hideBorder>
      <div className={styles.cardHeader}>
        <Text className={styles.cardTitle}>{title}</Text>
      </div>
      <Divider />
      <div className={styles.cardBody}>
        <GapStatement
          reason={gap.reason}
          index={index}
          onReasonChange={(reason) => onReasonChange(index, reason)}
          onNavigateToSection={onNavigateToSection}
          activityType={activityType}
          setActivityType={setActivityType}
          includesEducation={includesEducation}
        />
      </div>
      {isGapReferenceRequired && (
        <div>
          <Divider />
          <div className={styles.cardBody}>
            <GapReference
              index={index}
              schema={gap_reference_schema}
              gap_reference={gap_reference}
              onGapReferenceChange={(value) =>
                onGapReferenceChange(index, value)
              }
            />
          </div>
        </div>
      )}
      {isGapDocumentationRequired && (
        <div>
          <Divider />
          <div className={styles.cardBody}>
            <GapDocumentation
              index={index}
              schema={gap_documentation_schema}
              gap_documentation={gap_documentation}
              onGapDocumentationChange={(value) =>
                onGapDocumentationChange(index, value)
              }
            />
          </div>
        </div>
      )}
    </Card>
  );
}

const styles = {
  card: css({
    padding: 0,
    border: "1px solid var(--border-base)",
    borderRadius: "var(--border-radius-3)",
  }),
  cardHeader: css({
    background: "var(--background-accent-neutral-weakest, #F7F8F8)",
    padding: "var(--space-4)",
  }),
  cardTitle: css({
    fontSize: "var(--font-size-4)",
    fontWeight: 600,
  }),
  cardBody: css({
    padding: "var(--space-4)",
  }),
};
